import { ActionBar } from '@/components/layout/ActionBar';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Image } from 'expo-image';
import { ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { cssInterop } from 'nativewind';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useAppState } from '@/hooks/useAppState';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { MessagesService } from '~/services/domain/MessagesService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { FlashList } from '@shopify/flash-list';

import { MessageCard } from '@/components/domain/messages/MessageCard';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { MessagesView } from '@/models/Message';
import {
  MessageMultiple01Icon as MessageMultiple01SolidIcon,
  Search02Icon,
  Cancel02Icon,
} from '@hugeicons-pro/core-solid-rounded';
import { LicenseNoIcon, MessageMultiple01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { MessageListType } from '~/constants/lists';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { router, useLocalSearchParams, usePathname } from 'expo-router';
import { MessageSearch } from '~/components/domain/messages/MessageSearch';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { HeaderMenuSelector, HeaderMenuSelectorTitle } from '@/components/ui/HeaderMenuSelector';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { getMessageListItems } from '@/components/domain/messages/messageListItems';
import { VStack } from '../../gluestack/vstack';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Box } from '@/components/gluestack/box';
import { HStack } from '@/components/gluestack/hstack';
import { ConversationScreen } from '@/components/domain/messages/ConversationScreen';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { useSignalR, useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { useInfiniteListUtils, createSkeletonItems } from '@/hooks/useInfiniteListUtils';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useFormatCount } from '@/hooks/useFormatCount';
import { FloatingScrollButton } from '@/components/ui/FloatingScrollButton';
import { CountBadge } from '@/components/ui/CountAndMyTypeBadge';

cssInterop(Image, { className: 'style' });

const MESSAGE_LIST_COUNT_BADGE_BG = 'bg-tertiary-500';

interface MessagesScreenProps {
  initialMessageType: MessageListType;
  messageId?: number;
}

export function MessagesScreen({
  initialMessageType,
  messageId: messageIdProp,
}: MessagesScreenProps) {
  const params = useLocalSearchParams();
  const { t } = useTranslation();
  const { formatCount } = useFormatCount();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const isScreenActive = useIsFocused();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 10, desktop: 13 },
  });
  const pathname = usePathname();
  const { setMessageId: setSignalRMessageId } = useSignalR();
  const { messageId: signalRMessageId, messageCount: signalRMessageCount } = useSignalRUpdates();

  const [messageType, setMessageType] = useState<MessageListType>(initialMessageType);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUsedMessageType, setLastUsedMessageType] = useState<MessageListType>('inbox');
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(messageIdProp || null);
  const [isScrolled, setIsScrolled] = useState(false);

  // When opened from a push notification, the inbox is mounted only as a back-target
  // for the conversation pushed on top of it. Skip the initial fetch in that case
  // and let it kick in once the user dismisses the conversation (focus returns).
  const [allowFetch, setAllowFetch] = useState(() => params.deferLoad !== '1');
  const hasBeenBlurredRef = useRef(false);

  useEffect(() => {
    if (allowFetch) return;
    if (!isScreenActive) {
      hasBeenBlurredRef.current = true;
    } else if (hasBeenBlurredRef.current) {
      setAllowFetch(true);
      // Drop the one-shot flag from the URL so reloads/shares don't carry it forward.
      router.setParams({ deferLoad: undefined });
    }
  }, [isScreenActive, allowFetch]);

  const isSearchQueryUndefined = messageType === 'search' && searchQuery.trim() === '';

  const messageListItems = useMemo(() => getMessageListItems(t), [t]);

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  // Update selected message when URL changes
  useEffect(() => {
    const pathParts = pathname.split('/');
    const idFromPath = pathParts[pathParts.length - 1];
    const numericId = parseInt(idFromPath, 10);

    if (!isNaN(numericId)) {
      setSelectedMessageId(numericId);
    }
  }, [pathname]);

  const buildListRequestParams = useCallback(
    (pageParam: PageParam) => ({
      ...buildPaginationParams(pageParam, pageSize),
      ...(messageType === 'search' ? { search: searchQuery } : {}),
    }),
    [messageType, searchQuery, pageSize]
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['messages', messageType, searchQuery],
    queryFn: async ({ pageParam }) => {
      return await MessagesService.getMessages(messageType, buildListRequestParams(pageParam));
    },
    enabled: allowFetch && !isSearchQueryUndefined,
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<MessagesView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.MessageItems);
      return { ...data, items };
    }, []),
  });

  const itemCount = data?.pages?.[0]?.ItemCount ?? 0;
  const showInboxCountBadges =
    messageType === 'inbox' &&
    allowFetch &&
    !isSearchQueryUndefined &&
    !isPending &&
    !error &&
    data !== undefined;

  const showNonInboxListCountBadge =
    messageType !== 'inbox' &&
    allowFetch &&
    !isSearchQueryUndefined &&
    !isPending &&
    !error &&
    data !== undefined;

  const messageListCountBadges = (
    <>
      {showInboxCountBadges && (
        <HStack className="items-center gap-2">
          {isLargeScreen && (
            <CountBadge
              labelValue={formatCount(itemCount)}
              labelCaption={t('messages.inboxBadgeMessagesCaption', { count: itemCount })}
              count={itemCount}
              hideWhenZero
              size="sm"
              solidBgClassName={MESSAGE_LIST_COUNT_BADGE_BG}
              className="max-lg:hidden"
            />
          )}
          <CountBadge
            labelValue={formatCount(signalRMessageCount)}
            labelCaption={t('messages.inboxBadgeUnreadCaption', {
              count: signalRMessageCount,
            })}
            count={signalRMessageCount}
            hideWhenZero
            size="sm"
            solidBgClassName={MESSAGE_LIST_COUNT_BADGE_BG}
          />
        </HStack>
      )}
      {showNonInboxListCountBadge && (
        <CountBadge
          count={itemCount}
          hideWhenZero
          size="sm"
          abbreviate
          solidBgClassName={MESSAGE_LIST_COUNT_BADGE_BG}
        />
      )}
    </>
  );

  const { handleRefresh, isUserRefreshing, showHeaderRefetchIndicator, scrollToTop, onScroll } =
    useInfiniteListUtils({
      queryKey: ['messages', messageType, searchQuery],
      flashListRef,
      pageKey: `/messages/${messageType}`, // Unique key for scroll position persistence and navigation refresh
      onRefreshComplete: () => {
        // Also refresh conversation if one is selected on a large screen
        if (isLargeScreen && selectedMessageId) {
          const currentId = selectedMessageId;
          setSelectedMessageId(null);
          // Use a timeout to ensure React processes the state update and re-renders the component
          setTimeout(() => setSelectedMessageId(currentId), 0);
        }
      },
    });

  // Catch-up sync for windows where updates could have been missed. The list is
  // ordered by latest activity, so any conversation that changed has moved into
  // the head page — fetching just that page and merging it over the cache keeps
  // the cost at one request no matter how many pages are loaded, and leaves the
  // scroll position untouched. Conversations that moved into the fresh head are
  // dropped from deeper pages; deeper rows keep their cached state until opened.
  const lastHeadSyncAtRef = useRef(0);
  const syncListHead = useCallback(async () => {
    const queryKey = ['messages', messageType, searchQuery];
    const before = queryClient.getQueryData<InfiniteData<MessagesView, PageParam>>(queryKey);
    // An empty cache means the initial fetch owns this list, and an active query
    // fetch (initial page, next page, or refresh) will deliver fresher data than
    // this sync — merging around either would hand the list a stale head.
    if (!before || queryClient.getQueryState(queryKey)?.fetchStatus !== 'idle') return;
    lastHeadSyncAtRef.current = Date.now();
    try {
      const freshHead = await MessagesService.getMessages(
        messageType,
        buildListRequestParams(undefined)
      );
      const headIds = new Set(freshHead.MessageItems.map((message) => message.Id));
      queryClient.setQueryData<InfiniteData<MessagesView, PageParam>>(queryKey, (oldData) => {
        if (!oldData) return oldData;
        // A competing writer (pull-to-refresh, fetchNextPage, a cache update)
        // landed while the head request was in flight; its data is fresher or its
        // commit would discard this merge, so let it win — a later trigger resyncs.
        if (oldData !== before || queryClient.getQueryState(queryKey)?.fetchStatus !== 'idle') {
          return oldData;
        }
        // Old head rows pushed past the fresh page's boundary by new arrivals
        // exist in no other page — keep them below the fresh head, along with
        // the old pagination boundary, so no conversation vanishes and the next
        // page fetch neither skips nor re-returns rows.
        const oldFirstPage = oldData.pages[0];
        const leftovers = oldFirstPage.MessageItems.filter(
          (message) => !headIds.has(message.Id)
        );
        const mergedHead = {
          ...freshHead,
          MessageItems: [...freshHead.MessageItems, ...leftovers],
          ...(leftovers.length > 0
            ? { NextPageSkip: oldFirstPage.NextPageSkip, Cursor: oldFirstPage.Cursor }
            : {}),
        };
        return {
          ...oldData,
          pages: [
            mergedHead,
            ...oldData.pages.slice(1).map((page) => ({
              ...page,
              MessageItems: page.MessageItems.filter((message) => !headIds.has(message.Id)),
            })),
          ],
        };
      });
    } catch {
      // Background catch-up: keep the cached list on failure, a later trigger retries.
    }
  }, [messageType, searchQuery, buildListRequestParams, queryClient]);

  // Foreground and focus triggers fire back-to-back when reopening the app; the
  // timestamp guard collapses them into a single fetch (as in ConversationScreen).
  const syncListHeadGuarded = useCallback(() => {
    if (Date.now() - lastHeadSyncAtRef.current < 2500) return;
    void syncListHead();
  }, [syncListHead]);

  // Tracks whether `isScreenActive` was true on the previous run of the SignalR
  // effect, so we can distinguish "message arrived while already on the inbox"
  // from "message arrived while user was on the conversation, then they came back".
  const prevSignalRIsScreenActiveRef = useRef(isScreenActive);

  // Handle SignalR updates for the messages list
  useEffect(() => {
    const wasActive = prevSignalRIsScreenActiveRef.current;
    prevSignalRIsScreenActiveRef.current = isScreenActive;

    if (!signalRMessageId || !isScreenActive) return;

    // Phone-only short-circuit: if the screen JUST regained focus (e.g. user
    // closed a conversation they received a message in), the focus-sync effect
    // below will already sync the inbox head. Drop the SignalR signal so we
    // don't issue a second identical request 1s later. Tablets keep the SignalR
    // sync because their inbox stays focused in split view and the focus-sync
    // effect is gated by `!isLargeScreen`.
    if (!wasActive && !isLargeScreen) {
      setSignalRMessageId(null);
      return;
    }

    // Wait 1 second before syncing to ensure the read status is updated
    const timeoutId = setTimeout(async () => {
      await syncListHead();

      // Reset the SignalR message ID to prevent re-triggering
      setSignalRMessageId(null);
    }, 1000);

    // Cleanup timeout if component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [signalRMessageId, isScreenActive, isLargeScreen, syncListHead, setSignalRMessageId]);

  const hasUnreadMessagesAtTop =
    data?.items?.slice(0, 1).some((message) => message && !message.IsRead) ?? false;
  const showUnreadButton = isScrolled && hasUnreadMessagesAtTop;

  // Auto-scroll to top when new unread messages arrive and user is near the top
  useEffect(() => {
    if (!isScrolled && hasUnreadMessagesAtTop) {
      scrollToTop(true);
    }
  }, [data?.items]);

  // Skip the head sync on the same tick that `allowFetch` flips to true:
  // useInfiniteQuery's enable transition already triggers the initial fetch, so
  // syncing here too would produce a duplicate request. The flag is cleared
  // inline by the first invocation of the focus-sync effect after activation,
  // so the very first focus-sync tick is suppressed and all subsequent ones run
  // normally.
  const skipNextFocusSyncRef = useRef(!allowFetch);

  // Sync the inbox head when returning to MessagesScreen on phones
  useEffect(() => {
    if (!allowFetch) return;
    if (skipNextFocusSyncRef.current) {
      // Activation tick: useInfiniteQuery is already firing the initial fetch.
      skipNextFocusSyncRef.current = false;
      return;
    }
    if (isScreenActive && !isLargeScreen) {
      syncListHeadGuarded();
    }
  }, [allowFetch, isScreenActive, isLargeScreen, syncListHeadGuarded]);

  // Sync the inbox head when the app comes to the foreground
  useAppState({
    onForeground: useCallback(() => {
      if (!allowFetch || skipNextFocusSyncRef.current) return;
      if (isScreenActive) {
        syncListHeadGuarded();
      }
    }, [allowFetch, isScreenActive, syncListHeadGuarded]),
  });

  // Check for search query in URL on component mount
  useEffect(() => {
    const queryParam = params.q as string;

    if (queryParam) {
      // If there's a search query in the URL, set it as the current search
      setSearchQuery(queryParam);
      setMessageType('search');
    } else if (messageType === 'search') {
      setSearchQuery('');
    }
  }, [params.q]);

  const handleScroll = (event: { nativeEvent: { contentOffset: { y: number } } }) => {
    const scrollOffset = event.nativeEvent.contentOffset.y;
    if (scrollOffset > 100) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
    onScroll?.(event);
  };

  // Stable identity so every visible MessageCard isn't re-rendered (memo defeated)
  // whenever this screen re-renders.
  const handleMessageDeleted = useCallback((messageId: number) => {
    setSelectedMessageId((prev) => (prev === messageId ? null : prev));
  }, []);

  const navigateToMessageType = (type: MessageListType) => {
    if (type === messageType) return;

    queryClient.cancelQueries({ queryKey: ['messages', messageType, searchQuery] });

    if (isLargeScreen) {
      // Use expo router to replace the URL for large screens
      router.replace(`/messages/${type}`);
      // Clear selected message when switching message types
      setSelectedMessageId(null);
    } else {
      setLastUsedMessageType(messageType);
      setSearchQuery('');
      setMessageType(type);
      scrollToTop();
      if (Platform.OS === 'web') {
        window.history.pushState({}, '', `/messages/${type}`);
      }
    }
  };

  const shouldShowRefreshButton = !(messageType === 'search' && searchQuery === '');

  const ActionButtons = () => (
    <>
      {messageType !== 'search' && (
        <ButtonWrapper
          icon={Search02Icon}
          size="sm"
          onPress={() => navigateToMessageType('search')}
        />
      )}
      {messageType === 'search' && (
        <ButtonWrapper
          icon={Cancel02Icon}
          size="sm"
          action="neutral"
          iconClassName="color-typography-500"
          onPress={() => navigateToMessageType(lastUsedMessageType)}
        />
      )}
    </>
  );

  const handleMessagePress = useCallback(
    (messageId: number) => {
      if (isLargeScreen) {
        // On large screens, set the selected message for the split view
        setSelectedMessageId(messageId);
        if (Platform.OS === 'web') {
          window.history.pushState({}, '', `/messages/${messageId}`);
        } else {
          router.replace(`/messages/${messageId}`);
        }
      } else {
        // On small screens, navigate to the conversation route
        router.push(`/messages/${messageId}`);
      }
    },
    [isLargeScreen]
  );

  const renderMessagesContent = () => {
    const skeletonItems = createSkeletonItems(5);
    if (error) {
      return <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />;
    }

    if (isSearchQueryUndefined) {
      return undefined;
    }

    if (!isPending && data.items.length === 0) {
      return (
        <MessageView
          type="warning"
          text={messageType === 'deleted' ? t('messages.emptyDeleted') : t('messages.empty')}
          icon={LicenseNoIcon}
        />
      );
    }

    return (
      <Box className={`flex-1 ${isLargeScreen ? 'bg-background-100 dark:bg-black' : ''}`}>
        <FlashList
          ref={flashListRef}
          data={isPending ? skeletonItems : data.items}
          renderItem={({ item, index }) =>
            isPending ? (
              <MessageCard />
            ) : (
              <MessageCard
                message={item}
                listType={messageType}
                onPress={handleMessagePress}
                isSelected={isLargeScreen && selectedMessageId === item.Id}
                key={`message-${item.Id}`}
                onMessageDeleted={handleMessageDeleted}
              />
            )
          }
          keyExtractor={(item, index) => (isPending ? `s-${index}` : `message-${item.Id}`)}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          onEndReached={() => {
            if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={onEndReachedThreshold}
          maintainVisibleContentPosition={{
            autoscrollToTopThreshold: 0.1,
          }}
          ListFooterComponent={() => {
            if (!isFetchingNextPage) return null;
            return (
              <Box className="m-4">
                <ActivityIndicator color="grey" />
              </Box>
            );
          }}
          refreshControl={
            <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
          }
          extraData={selectedMessageId}
        />
        <FloatingScrollButton
          isVisible={showUnreadButton}
          onPress={() => scrollToTop(true)}
          text={t('messages.unreadMessage')}
        />
      </Box>
    );
  };

  const renderConversationPanel = () => {
    if (!selectedMessageId) {
      return (
        <Box className="flex-1 items-center justify-center rounded-tl-xl bg-background-0">
          <HugeIconsWrapper
            icon={MessageMultiple01Icon}
            size={128}
            className="color-background-100"
          />
        </Box>
      );
    }

    return (
      <ConversationScreen
        key={selectedMessageId}
        messageId={selectedMessageId}
        disableScreenHeader={true}
      />
    );
  };

  // For large screens, render split view
  if (isLargeScreen) {
    const hasMessages =
      isPending || (data?.items && data.items.length > 0) || messageType === 'search';

    return (
      <HStack className="flex-1 gap-2">
        {/* Messages List Panel — flex ratio avoids w-1/3 + mr + w-2/3 overflowing parent on native */}
        <VStack className={hasMessages ? 'min-w-0 flex-1' : 'flex-1'}>
          <ScreenHeader
            options={{
              hideBackButton: true,
              title: t('messages.title'),
              headerTitle: () => (
                <HeaderMenuSelectorTitle
                  value={messageType}
                  items={messageListItems}
                  title={t('messages.title')}
                  icon={MessageMultiple01SolidIcon}
                />
              ),
              headerLeft: () => (
                <HStack className="items-center gap-4">
                  <HeaderMenuSelector
                    value={messageType}
                    onChange={navigateToMessageType}
                    items={messageListItems}
                    triggerLabel={t(`messages.${messageType}`)}
                    triggerVariant="button"
                  />
                  {messageListCountBadges}
                  {showHeaderRefetchIndicator && <ActivityIndicator color="grey" />}
                </HStack>
              ),
              headerRight: () => (
                <HStack className="items-center gap-3">
                  <ActionButtons />
                  <HeaderRefreshButton
                    isVisible={shouldShowRefreshButton}
                    isLargeScreen={isLargeScreen}
                    isRefreshing={isUserRefreshing}
                    isDisabled={isRefetching}
                    onPress={handleRefresh}
                  />
                </HStack>
              ),
            }}
          />

          {messageType === 'search' && (
            <MessageSearch
              searchQuery={searchQuery}
              onSearchQueryChange={(query) => {
                setSearchQuery(query);
                if (Platform.OS === 'web') {
                  window.history.pushState(
                    {},
                    '',
                    `/messages/search${query.trim() !== '' ? `?q=${encodeURIComponent(query)}` : ''}`
                  );
                }
              }}
              autoFocus={true}
            />
          )}

          {renderMessagesContent()}
        </VStack>

        {/* Conversation Panel */}
        {hasMessages && <VStack className="min-w-0 flex-[2]">{renderConversationPanel()}</VStack>}
      </HStack>
    );
  }

  // For small screens, render single view
  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={
          {
            title: t(`messages.title`),
            headerRight: () => (
              <HeaderRefreshButton
                isVisible={shouldShowRefreshButton}
                isLargeScreen={isLargeScreen}
                isRefreshing={isUserRefreshing}
                isDisabled={isRefetching}
                inAppHeaderBar
                onPress={handleRefresh}
              />
            ),
            headerBottom: () => (
              <ActionBar
                leftSection={
                  <HStack className="items-center gap-3">
                    <HeaderMenuSelector
                      value={messageType}
                      onChange={navigateToMessageType}
                      items={messageListItems}
                      triggerLabel={t(`messages.${messageType}`)}
                    />
                    {messageListCountBadges}
                  </HStack>
                }
                rightSection={<ActionButtons />}
              />
            ),
          } as any
        }
      />

      {messageType === 'search' && (
        <MessageSearch
          searchQuery={searchQuery}
          onSearchQueryChange={(query) => {
            setSearchQuery(query);
            if (Platform.OS === 'web') {
              window.history.pushState(
                {},
                '',
                `/messages/search${query.trim() !== '' ? `?q=${encodeURIComponent(query)}` : ''}`
              );
            }
          }}
          autoFocus={true}
        />
      )}

      {renderMessagesContent()}
    </VStack>
  );
}
